import { useState, useRef, useEffect } from 'react';
import { usePreferences } from '../context/PreferencesContext';
import { signOut, changePassword } from '../auth';

function ChangePasswordModal({ onClose }) {
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (form.next !== form.confirm) { setError('New passwords do not match'); return; }
    if (form.next.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError(''); setLoading(true);
    try {
      await changePassword(form.current, form.next);
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>🔑 Change Password</h3>
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">Password changed successfully!</div>}
        {!success && (
          <form onSubmit={handle}>
            <div className="form-group">
              <label>Current Password</label>
              <input type="password" value={form.current} onChange={set('current')} required autoFocus />
            </div>
            <div className="form-group">
              <label>New Password</label>
              <input type="password" value={form.next} onChange={set('next')} required placeholder="Min 8 characters" />
            </div>
            <div className="form-group">
              <label>Confirm New Password</label>
              <input type="password" value={form.confirm} onChange={set('confirm')} required />
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Changing…' : 'Change Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function UserMenu({ email, onShowSettings, onSignOut }) {
  const [open, setOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const { darkMode, toggleDarkMode, viewMode, toggleViewMode } = usePreferences();
  const ref = useRef();
  const initials = email ? email.slice(0, 2).toUpperCase() : '??';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      <div ref={ref} style={{ position: 'relative' }}>
        <div className="user-avatar" onClick={() => setOpen(o => !o)} title={email}>
          {initials}
        </div>

        {open && (
          <div className="user-menu-dropdown">
            <div className="user-menu-header">
              <strong>{email}</strong>
              Signed in
            </div>

            <button className="user-menu-item" onClick={toggleDarkMode}>
              <span><span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
              <div className={`toggle-switch${darkMode ? ' on' : ''}`} />
            </button>

            <button className="user-menu-item" onClick={toggleViewMode}>
              <span><span className="menu-icon">{viewMode === 'card' ? '☰' : '⊞'}</span>{viewMode === 'card' ? 'List View' : 'Card View'}</span>
              <div className={`toggle-switch${viewMode === 'list' ? ' on' : ''}`} />
            </button>

            <div className="user-menu-divider" />

            <button className="user-menu-item" onClick={() => { setOpen(false); setShowChangePassword(true); }}>
              <span><span className="menu-icon">🔑</span>Change Password</span>
            </button>

            <button className="user-menu-item" onClick={() => { setOpen(false); onShowSettings(); }}>
              <span><span className="menu-icon">⚙️</span>Platform Settings</span>
            </button>

            <div className="user-menu-divider" />

            <button className="user-menu-item" style={{ color: 'var(--red)' }}
              onClick={() => { setOpen(false); signOut(); onSignOut(); }}>
              <span><span className="menu-icon">→</span>Sign Out</span>
            </button>
          </div>
        )}
      </div>

      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
    </>
  );
}
